import {Table, Text} from '@mantine/core';
import React from 'react';

import {DiffTable} from 'src/design/molecules/DiffTable';
import {DiffCell} from 'src/design/atoms/DiffCell';

/**
 * Renders a table of added/updated/deleted field changes, diffing each
 * key's Original vs Current value.
 * @registry Audit-style diff table — pass a `changes` map plus equality/format
 * callbacks, get rows with the changed side highlighted.
 *
 * Ported from `library-m/data-display/ObjectDiffView`. DEVIATION: value
 * equality and formatting are no longer imported from
 * `src/shared/utils/auditValue` (banned in this tier) — callers inject
 * `valuesEqual`/`formatValue` props instead.
 */

export interface IChanges {
  [key: string]: {
    Current: unknown;
    Original: unknown;
    Visible?: boolean;
  };
}

// Recursive function to render nested differences
const renderDiffs = (
  changeType: 'added' | 'updated' | 'deleted',
  changes: IChanges,
  valuesEqual: (a: unknown, b: unknown) => boolean,
  formatValue: (v: unknown) => string,
  parentKey: string = ''
): JSX.Element[] => {
  const elements: JSX.Element[] = [];

  for (const key in changes) {
    if (Object.prototype.hasOwnProperty.call(changes, key)) {
      const {Current, Original, Visible} = changes[key];
      if (Visible === false) {
        continue;
      }

      const fullPath = parentKey ? `${parentKey}.${key}` : key;
      const isDifferent =
        changeType === 'added' || changeType === 'deleted'
          ? true
          : !valuesEqual(Original, Current);

      const displayOriginal = changeType !== 'added' ? formatValue(Original) : '';
      const displayCurrent = changeType !== 'deleted' ? formatValue(Current) : '';
      const displayTitle =
        fullPath.indexOf('.') < 0 ? fullPath.splitUppercase() : fullPath;

      elements.push(
        <Table.Tr key={fullPath}>
          <Table.Td>
            <Text>{displayTitle}</Text>
          </Table.Td>
          <DiffCell side="original" isDifferent={isDifferent}>
            {displayOriginal}
          </DiffCell>
          <DiffCell side="current" isDifferent={isDifferent}>
            {displayCurrent}
          </DiffCell>
        </Table.Tr>
      );
    }
  }

  return elements;
};

export interface ObjectDiffViewProps {
  changeType: 'added' | 'updated' | 'deleted';
  changes: IChanges;
  valuesEqual: (a: unknown, b: unknown) => boolean;
  formatValue: (v: unknown) => string;
}
export const ObjectDiffView: React.FC<ObjectDiffViewProps> = ({
  changeType,
  changes,
  valuesEqual,
  formatValue,
}) => {
  return (
    <DiffTable>{renderDiffs(changeType, changes, valuesEqual, formatValue)}</DiffTable>
  );
};
